import { Component, ViewChild, inject } from '@angular/core';
import { ComponentAnchor, ComponentVersion } from 'src/models/anchor-and-version/component.model';
import { FormControl, ValidationErrors } from '@angular/forms';
import { ComponentSegmentRelationTableComponent } from './component-segment-relation-table/component-segment-relation-table.component';
import { CrudBaseModalComponent } from '../crud-base-modal/crud-base-modal.component';
import { CrudModalAbstractComponent } from '../crud-base-modal/crud-modal-abstract.component';
import { ModalProps } from '../crud-base-modal/modal-field.interface';
import { NotificationService } from 'src/services/notification.service';

@Component({
    selector: 'app-crud-component-modal',
    template: `
        <app-crud-base-modal #baseModal [modalProps]="modalProps">
            <app-component-segment-relation-table
                #segmentRelationTable
                (rowsChanged)="onSegmentRelationsChanged()"
            ></app-component-segment-relation-table>
            <div modal-validation class="text-left" aria-live="polite">
                @if (
                    form && form.get('__segmentRelations')?.getError('segmentRelations');
                    as message
                ) {
                    <small class="text-red-500 text-xs">{{ message }}</small>
                }
            </div>
        </app-crud-base-modal>
    `,
    imports: [CrudBaseModalComponent, ComponentSegmentRelationTableComponent],
})
export class CrudComponentModalComponent extends CrudModalAbstractComponent<
    ComponentAnchor,
    ComponentVersion
> {
    @ViewChild('segmentRelationTable')
    public segmentRelationTable?: ComponentSegmentRelationTableComponent;

    public modalProps: ModalProps = {
        title: 'Component',
        validateNameFormat: true,
        afterSave: (savedItem?: unknown) => this.saveSegmentRelations(savedItem),
        insertContentAfter: 'isActive',
        anchorFields: [
            {
                field: 'componentCodeAnchorKey',
                label: 'Component Code',
                type: 'lazy-dropdown',
                required: true,
                order: 2,
            },
        ],
        versionFields: [
            {
                field: 'changeRequestKey',
                label: 'Change Request',
                type: 'lazy-dropdown',
                required: true,
                order: 1,
            },
            { field: 'name', label: 'Name', type: 'text', required: true, order: 4 },
            { field: 'fullName', label: 'Full Name', type: 'text', order: 5 },
            { field: 'generation', label: 'Generation', type: 'number', defaultValue: 0, order: 6 },
            { field: 'owner', label: 'Owner', type: 'text', order: 7 },
            { field: 'ownerId', label: 'Owner ID', type: 'text', order: 8 },
            { field: 'ownerGroup', label: 'Owner Group', type: 'text', order: 9 },
            {
                field: 'primaryAddressAnchorKey',
                label: 'Primary address',
                type: 'lazy-dropdown',
                order: 10,
            },
            { field: 'documentation', label: 'Documentation', type: 'textarea', order: 11 },
            { field: 'comment', label: 'Comment', type: 'textarea', order: 12 },
            { field: 'isActive', label: 'Is Active', type: 'switch', order: 13 },
        ],
    };

    private readonly notificationService = inject(NotificationService);
    private readonly segmentRelationsControlName = '__segmentRelations';

    public override onOpen(): void {
        void this.openComponentModal();
    }

    public onSegmentRelationsChanged(): void {
        if (!this.baseModal) return;

        this.updateSegmentRelationsValidity(true);
        this.baseModal.hasUnsavedChanges = true;
    }

    private getComponentAnchorKey(savedItem?: unknown): number | undefined {
        const anchorKey = (this.modalProps.anchor as Record<string, unknown> | undefined)?.['key'];
        if (typeof anchorKey === 'number') return anchorKey;

        const savedItemKey = (savedItem as Record<string, unknown> | undefined)?.['key'];
        return typeof savedItemKey === 'number' ? savedItemKey : undefined;
    }

    private getSegmentRelationsValidationErrors(): ValidationErrors | null {
        const validationMessage = this.segmentRelationTable?.getValidationMessage();
        return validationMessage ? { segmentRelations: validationMessage } : null;
    }

    private async openComponentModal(): Promise<void> {
        await this.baseModal?.onOpen();
        await new Promise((resolve) => setTimeout(resolve, 0));
        this.registerSegmentRelationsControl();
        await this.segmentRelationTable?.load(
            this.modalProps.anchor as ComponentAnchor | undefined
        );
        this.updateSegmentRelationsValidity();
    }

    private registerSegmentRelationsControl(): void {
        if (!this.form) return;

        const existingControl = this.form.get(this.segmentRelationsControlName);
        if (existingControl) {
            existingControl.setValidators(() => this.getSegmentRelationsValidationErrors());
            this.updateSegmentRelationsValidity();
            return;
        }

        this.form.addControl(
            this.segmentRelationsControlName,
            new FormControl(true, () => this.getSegmentRelationsValidationErrors())
        );
        this.updateSegmentRelationsValidity();
    }

    private async saveSegmentRelations(savedItem?: unknown): Promise<void> {
        const componentAnchorKey = this.getComponentAnchorKey(savedItem);
        const changeRequestKey = Number(this.form?.get('changeRequestKey')?.value ?? 0);

        if (!componentAnchorKey || Number.isNaN(changeRequestKey)) {
            throw new Error(
                'Cannot save segment relations because component or change request is missing.'
            );
        }

        const hadChanges = this.segmentRelationTable?.hasChanges() ?? false;
        await this.segmentRelationTable?.save(componentAnchorKey, changeRequestKey);
        if (hadChanges) {
            this.notificationService.showSuccess('Component segment relations saved successfully');
        }
    }

    private updateSegmentRelationsValidity(markDirty = false): void {
        const control = this.form?.get(this.segmentRelationsControlName);
        if (!control) return;

        if (markDirty) {
            control.markAsDirty();
        }
        control.updateValueAndValidity({ emitEvent: false });
    }
}
